import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Reads the public dashboard id from the current URL, if the user is currently
 * viewing a public-dashboard route (e.g. `/pages/public-dashboard/:id`).
 *
 * Returns `null` for any other route so this interceptor stays a no-op outside
 * the public-dashboard experience.
 *
 * Implemented as a URL parser rather than a router/service lookup so it works
 * cleanly inside an interceptor (which runs as a free function and shouldn't
 * pull heavyweight services into its DI graph).
 */
function readPublicDashboardIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const path = window.location.pathname || '';
    // Match `/pages/public-dashboard/<id>` and capture the id segment.
    const match = path.match(/\/public-dashboard\/([^\/?#]+)/);
    return match?.[1] ?? null;
}

/**
 * Adds the `x-public-dashboard-id` header to outgoing API requests when the
 * user is viewing a public dashboard page. The server uses this header to
 * grant a narrow, library-scoped bypass of session auth for the small set of
 * read endpoints the public dashboard widgets need.
 *
 * Only applies to `/api/...` URLs; everything else passes through unchanged.
 */
export function publicDashboardInterceptor(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith('/api/') && !req.url.includes('/api/')) {
        return next(req);
    }

    const dashboardId = readPublicDashboardIdFromUrl();
    if (!dashboardId) {
        return next(req);
    }

    const cloned = req.clone({
        setHeaders: { 'x-public-dashboard-id': dashboardId }
    });
    return next(cloned);
}
